import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Mail, UserRound } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AuthShell } from '@/components/auth/AuthShell';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { GoogleButton } from '@/components/auth/GoogleButton';
import { FieldError } from '@/components/ui/FieldError';
import { useAuth } from '@/hooks/useAuth';
import { registerSchema, type RegisterInput } from '@/types/schemas';

type Strength = 'weak' | 'medium' | 'strong';

function calcStrength(pw: string): Strength {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[0-9]/.test(pw) && /[a-zA-Z]/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;
  if (score <= 1) return 'weak';
  if (score <= 2) return 'medium';
  return 'strong';
}

const STRENGTH_LABEL: Record<Strength, { text: string; color: string; segments: number }> = {
  weak: { text: 'Weak — add numbers and symbols', color: '#f87171', segments: 1 },
  medium: { text: 'Medium — try a longer password', color: '#fb923c', segments: 2 },
  strong: { text: 'Strong password ✓', color: '#CBFF47', segments: 3 },
};

export function RegisterPage() {
  const { register: doRegister, user, loading } = useAuth();
  const navigate = useNavigate();

  const [submitError, setSubmitError] = useState('');
  const [emailServerError, setEmailServerError] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const password = useWatch({ control, name: 'password' });
  const strength = useMemo(() => (password ? calcStrength(password) : null), [password]);

  if (!loading && user) {
    return <Navigate to="/" replace />;
  }

  const onSubmit = handleSubmit(async (data) => {
    setSubmitError('');
    setEmailServerError(false);
    const res = await doRegister({
      email: data.email.trim(),
      password: data.password,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
    });
    if (res.ok) {
      navigate('/', { replace: true });
    } else {
      if (res.error === 'user_already_exists') {
        setEmailServerError(true);
        setError('email', { type: 'server', message: 'Email already registered' });
      } else {
        setSubmitError(res.message ?? 'Registration failed');
      }
    }
  });

  const perks = [
    'Track every workout',
    'Set goals & watch progress',
    'Connect Strava & health apps',
    'Free forever · No credit card',
  ];

  return (
    <AuthShell
      leftHeading={
        <>
          Join
          <br />
          the
          <br />
          team.
        </>
      }
      leftBelow={
        <div className="reg-perks">
          {perks.map((p) => (
            <div className="reg-perk" key={p}>
              <div className="reg-perk-dot" />
              <div className="reg-perk-text">{p}</div>
            </div>
          ))}
        </div>
      }
      formHeader={
        <>
          <h2>Create account</h2>
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--dim)',
              marginTop: 4,
            }}
          >
            Free forever · No credit card
          </p>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate>
        <div className="form-group">
          <label htmlFor="first-name">First Name</label>
          <div className="input-wrapper">
            <UserRound size={20} strokeWidth={2} />
            <input
              type="text"
              id="first-name"
              className="form-input"
              placeholder="John"
              autoComplete="given-name"
              aria-invalid={!!errors.firstName}
              {...register('firstName')}
            />
          </div>
          {errors.firstName && <FieldError message={errors.firstName.message} />}
        </div>

        <div className="form-group">
          <label htmlFor="last-name">Last Name</label>
          <div className="input-wrapper">
            <UserRound size={20} strokeWidth={2} />
            <input
              type="text"
              id="last-name"
              className="form-input"
              placeholder="Doe"
              autoComplete="family-name"
              aria-invalid={!!errors.lastName}
              {...register('lastName')}
            />
          </div>
          {errors.lastName && <FieldError message={errors.lastName.message} />}
        </div>

        <div className="form-group">
          <label htmlFor="email">Email</label>
          <div className="input-wrapper">
            <Mail size={20} strokeWidth={2} />
            <input
              type="email"
              id="email"
              className="form-input"
              placeholder="your@email.com"
              autoComplete="email"
              aria-invalid={!!errors.email}
              style={emailServerError ? { borderColor: 'var(--danger)' } : undefined}
              {...register('email')}
            />
          </div>
          {errors.email && <FieldError message={errors.email.message} />}
        </div>

        <div className="form-group">
          <label htmlFor="password">Password</label>
          <PasswordInput
            id="password"
            placeholder="Create a password"
            autoComplete="new-password"
            registration={register('password')}
            invalid={!!errors.password}
          />
          {errors.password && <FieldError message={errors.password.message} />}
          {strength && !errors.password && (
            <div className="password-strength" style={{ display: 'flex' }}>
              <div className="strength-bar-row">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className={
                      i < STRENGTH_LABEL[strength].segments
                        ? `strength-bar-seg filled-${strength}`
                        : 'strength-bar-seg'
                    }
                  />
                ))}
              </div>
              <span className="strength-label" style={{ color: STRENGTH_LABEL[strength].color }}>
                {STRENGTH_LABEL[strength].text}
              </span>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="confirm-password">Confirm Password</label>
          <PasswordInput
            id="confirm-password"
            placeholder="Confirm your password"
            autoComplete="new-password"
            registration={register('confirmPassword')}
            invalid={!!errors.confirmPassword}
          />
          {errors.confirmPassword && <FieldError message={errors.confirmPassword.message} />}
        </div>

        {submitError && <FieldError message={submitError} />}

        <button type="submit" className="submit-btn" disabled={isSubmitting}>
          {isSubmitting ? 'Creating account…' : 'Create account →'}
        </button>

        <div className="divider">
          <span>or</span>
        </div>

        <GoogleButton label="Sign up with Google" />

        <div className="footer-text">
          Already have an account? <Link to="/login">Log in →</Link>
        </div>
      </form>
    </AuthShell>
  );
}
